import { useQuery } from '@tanstack/react-query'
import { Link, useRouterState } from '@tanstack/react-router'
import type { ArcanaReadingQueueResponseDto, LunarAuditRunsResponseDto } from '@lunary/api-client'
import { BrandWordmark } from '@lunary/ui-web/brand-wordmark'
import { Icon } from '@lunary/ui-web/icons'
import { MaintenanceScreen } from '@lunary/ui-web/maintenance-screen'
import { useOutage } from '@lunary/ui-web/outage'
import type { IconName } from '@lunary/ui-web/icons'
import { ThemeSwitch } from '@lunary/ui-web/theme-switch'
import { api } from '../api/client'
import { AuthPage } from '../../features/auth/page'
import { useAdminAuth } from '../auth/admin-auth-context'
import { useTheme } from '../theme/theme'
import { useThemeSync } from '../theme/use-theme-sync'
import styles from './shell.module.css'

type NavItem = { to: string; label: string; icon: IconName; adminOnly?: boolean; counter?: 'readings' | 'lunars' }

const NAV: ReadonlyArray<{ title: string; items: ReadonlyArray<NavItem> }> = [
  {
    title: 'Работа эксперта',
    items: [
      { to: '/daily-card', label: 'Карта дня', icon: 'today' },
      { to: '/daily-picks', label: 'Три карты', icon: 'deck' },
      { to: '/decks', label: 'Колоды', icon: 'deck' },
      { to: '/card-ratings', label: 'Отклик', icon: 'pick' },
      { to: '/arcana-readings', label: 'Разборы', icon: 'orders', counter: 'readings' },
      { to: '/reading-topics', label: 'Темы разборов', icon: 'pick' },
      { to: '/trainer', label: 'Студия', icon: 'spark' },
      { to: '/notifications', label: 'Уведомления', icon: 'mail' },
    ],
  },
  {
    title: 'Администрирование',
    items: [
      { to: '/overview', label: 'Обзор', icon: 'history', adminOnly: true },
      { to: '/lunars', label: 'Лунары', icon: 'pick', adminOnly: true, counter: 'lunars' },
      { to: '/users', label: 'Пользователи', icon: 'profile', adminOnly: true },
      { to: '/audit-log', label: 'Журнал действий', icon: 'history', adminOnly: true },
    ],
  },
]

const ROLE_LABELS: Readonly<Record<string, string>> = {
  admin: 'Администратор',
  expert: 'Эксперт',
}

export function Shell({ children }: { children: React.ReactNode }) {
  const { user, status, logout, retry } = useAdminAuth()
  const down = useOutage()
  const theme = useTheme()
  const changeTheme = useThemeSync(status === 'authenticated')
  const path = useRouterState({ select: (s) => s.location.pathname })
  const staff = status === 'authenticated' && (user?.role === 'expert' || user?.role === 'admin')
  const pending = useQuery({
    queryKey: ['arcana-readings', 'count'],
    queryFn: () => api.get<ArcanaReadingQueueResponseDto>('/admin/arcana-readings', { query: { filter: 'new', limit: 1 } }),
    enabled: staff,
    retry: false,
    refetchInterval: 120_000,
  })
  const findings = useQuery({
    queryKey: ['admin-lunars', 'audit-runs'],
    queryFn: () => api.get<LunarAuditRunsResponseDto>('/admin/lunars/audit/runs'),
    enabled: status === 'authenticated' && user?.role === 'admin',
    retry: false,
    refetchInterval: 120_000,
  })
  const counts: Readonly<Record<string, number>> = { readings: pending.data?.total ?? 0, lunars: findings.data?.open_findings ?? 0 }

  if (down) return <MaintenanceScreen />

  if (status === 'loading') {
    return <div className="spinner" aria-label="Загрузка" />
  }

  if (status !== 'authenticated' || !user) {
    return (
      <div className={styles.guest}>
        <span className={styles.guestBrand}>
          <BrandWordmark height={34} />
        </span>
        <main className={styles.guestMain}>
          {status === 'unavailable' ? (
            <div className="card-panel" role="alert">
              <h2>Сервер не отвечает</h2>
              <p className="muted">Не удалось проверить вход: API Lunary сейчас недоступен. Попробуйте через минуту.</p>
              <button type="button" onClick={retry}>
                Повторить
              </button>
            </div>
          ) : path.startsWith('/auth') ? (
            children
          ) : (
            <AuthPage />
          )}
        </main>
      </div>
    )
  }

  const isAdmin = user.role === 'admin' || user.role === 'expert'
  const groups = NAV.map((g) => ({ ...g, items: g.items.filter((i) => !i.adminOnly || isAdmin) })).filter(
    (g) => g.items.length > 0,
  )

  return (
    <div className={styles.app}>
      <aside className={styles.side}>
        <Link to="/" className={styles.brand} aria-label="Lunary, админка">
          <BrandWordmark height={30} />
          <span className={styles.brandSub}>Админка</span>
        </Link>
        <nav className={styles.nav} aria-label="Разделы">
          {groups.map((group) => (
            <div key={group.title} className={styles.group}>
              <span className={styles.groupTitle}>{group.title}</span>
              {group.items.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={styles.link}
                  aria-current={path.startsWith(item.to) ? 'page' : undefined}
                >
                  <Icon name={item.icon} size={18} stroke={1.6} />
                  {item.label}
                  {item.counter && (counts[item.counter] ?? 0) > 0 && (
                    <span className={styles.count} aria-label={`ждут: ${counts[item.counter]}`}>
                      {counts[item.counter]}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          ))}
        </nav>
        <div className={styles.footer}>
          <ThemeSwitch theme={theme} onChange={changeTheme} />
          <div className={styles.who}>
            <span className={styles.avatar} aria-hidden="true">
              {(user.display_name ?? user.email ?? '?').slice(0, 1).toUpperCase()}
            </span>
            <span className={styles.whoText}>
              <span className={styles.whoName}>{user.display_name ?? user.email ?? 'Без имени'}</span>
              <span className={styles.whoRole}>{ROLE_LABELS[user.role] ?? user.role}</span>
            </span>
            <button type="button" className={styles.logout} onClick={() => void logout()}>
              Выйти
            </button>
          </div>
        </div>
      </aside>
      <main className={styles.main}>{children}</main>
    </div>
  )
}
